import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _orderBy from 'lodash/orderBy';
import _chunk from 'lodash/chunk';

import Select from './Select';
import ZeroState from '../components/ZeroState';

import { Colors, FontSizes, FontWeights } from '../Theme';

const Table = ({
  apiPagination,
  columns,
  data,
  defaultSortColumn,
  defaultSortDirection,
  hideHeaders,
  onRowClick,
  onSortChange,
  perPage,
  placeholder,
  tableLayout
}) => {
  const styles = {
    component: {
      backgroundColor: Colors.WHITE
    },
    table: {
      width: '100%',
      marginTop: 1,
      tableLayout: tableLayout || 'auto'
    },
    header: {
      padding: 10,
      textAlign: 'left',
      fontWeight: FontWeights.MEDIUM,
      borderBottom: '1px solid #e5e5e5',
      borderTop: '1px solid #e5e5e5',
      textTransform: 'uppercase',
      fontSize: FontSizes.SMALL,
      letterSpacing: '1px',
      whiteSpace: 'nowrap',
      lineHeight: '22px'
    },
    sortable: {
      cursor: 'pointer',
      color: Colors.PRIMARY.hex
    },
    row: {
      cursor: onRowClick ? 'pointer' : 'default',
      transition: 'all .2s'
    },
    column: {
      padding: '20px 10px',
      verticalAlign: 'middle',
      wordBreak: 'break-word',
      lineHeight: '1.4em'
    },

    // PAGINATION
    pagination: {
      display: 'flex',
      textAlign: 'center',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 30
    },
    page_btn: {
      fontSize: 30,
      color: Colors.PRIMARY.hex,
      cursor: 'pointer',
      margin: '0 10px'
    },
    page_btn_inactive: {
      fontSize: 30,
      color: '#e5e5e5',
      margin: '0 10px'
    }
  };
  const [hover_index, setHoverIndex] = useState(null);
  const [sort_by_column, setSortByCol] = useState(defaultSortColumn || {});
  const [sort_direction, setSortDirection] = useState(defaultSortDirection || 'asc');
  const [page, setPage] = useState(1);
  const sort_method = sort_by_column.sort_by || sort_by_column.render || sort_by_column.id;
  const current_page = apiPagination
    ? parseInt(apiPagination.current_page, 10)
    : parseInt(page, 10);
  const sorted_data = onSortChange ? data : _orderBy(data, sort_method, sort_direction);
  const paged_data = perPage ? _chunk(sorted_data, perPage) : [sorted_data];
  const page_data = perPage ? paged_data[current_page - 1] || [] : paged_data[0];
  const total_pages = apiPagination
    ? parseInt(apiPagination.total_pages || 1, 10)
    : paged_data.length;
  const is_first_page = current_page === 1;
  const is_last_page = apiPagination ? apiPagination.last_page : current_page === total_pages;

  const handleColumnSort = column => {
    if (column.is_sortable) {
      const sort_dir = sort_direction === 'asc' ? 'desc' : 'asc';

      if (onSortChange) {
        onSortChange(column.id, sort_dir);
      } else {
        setSortByCol(column);
        setSortDirection(sort_dir);
      }
    }
  };

  const handleRowClick = row_object => {
    if (onRowClick) {
      onRowClick(row_object);
    }
  };

  const handlePageClick = page => {
    if (apiPagination) {
      apiPagination.onClick(page);
    } else {
      setPage(page);
    }
  };

  const handlePageChange = e => {
    if (apiPagination) {
      apiPagination.onClick(e.target.value);
    } else {
      setPage(e.target.value);
    }
  };

  return (
    <div style={styles.component}>
      <table style={styles.table}>
        {hideHeaders ? null : (
          <thead style={styles.thead}>
            <tr>
              {columns.map((column, i) => {
                const column_styles = Object.assign(
                  {},
                  styles.header,
                  column.width && { width: column.width },
                  column.is_sortable && styles.sortable,
                  column.styles
                );

                return (
                  <th key={i} onClick={handleColumnSort.bind(null, column)} style={column_styles}>
                    {column.label}
                    {column.is_sortable && sort_by_column.id === column.id ? (
                      <i
                        className={`mdi mdi-${
                          sort_direction === 'asc' ? 'chevron-up' : 'chevron-down'
                        }`}
                        style={{ marginLeft: 3, fontSize: 22, position: 'absolute' }}
                      />
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
        )}

        {page_data.length ? (
          <tbody style={styles.tbody}>
            {page_data.map((row_object, i) => {
              const bg_color = i % 2 === 0 ? '#fff' : '#f9f9f9';
              const row_styles = Object.assign(
                {
                  backgroundColor:
                    hover_index === i && onRowClick ? `rgba(${Colors.PRIMARY.rgb}, 0.1)` : bg_color,
                  fontStyle: row_object.is_archived ? 'italic' : ''
                },
                styles.row
              );

              return (
                <tr
                  key={i}
                  onClick={handleRowClick.bind(null, row_object)}
                  onMouseEnter={onRowClick ? setHoverIndex.bind(null, i) : null}
                  onMouseLeave={onRowClick ? setHoverIndex.bind(null, null) : null}
                  style={row_styles}
                >
                  {columns.map((column, i) => {
                    if (column.id === 'exampleName') {
                      return (
                        <td key={i} style={Object.assign({}, styles.column, column.styles)}>
                          <div style={{ fontWeight: '900' }}>
                            {column.render
                              ? column.render(row_object)
                              : row_object[column.id] || 'N/A'}
                          </div>
                        </td>
                      );
                    } else {
                      return (
                        <td key={i} style={Object.assign({}, styles.column, column.styles)}>
                          {column.render
                            ? column.render(row_object)
                            : row_object[column.id] || 'N/A'}
                        </td>
                      );
                    }
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : null}
      </table>

      {data.length && (apiPagination || perPage) ? (
        <div style={styles.pagination}>
          <i
            className='mdi mdi-chevron-left-circle'
            onClick={is_first_page ? null : handlePageClick.bind(null, current_page - 1)}
            style={is_first_page ? styles.page_btn_inactive : styles.page_btn}
          />

          <Select
            onChange={handlePageChange}
            options={Array.from(Array(total_pages).keys(), p => p + 1).map(p => ({
              label: `Page ${p}`,
              value: p
            }))}
            value={current_page}
          />

          <i
            className='mdi mdi-chevron-right-circle'
            onClick={is_last_page ? null : handlePageClick.bind(null, current_page + 1)}
            style={is_last_page ? styles.page_btn_inactive : styles.page_btn}
          />
        </div>
      ) : null}

      {!data.length ? (
        <ZeroState icon='archive-outline' title={placeholder || 'No Entries Available'} />
      ) : null}
    </div>
  );
};

Table.propTypes = {
  apiPagination: PropTypes.shape({
    current_page: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    total_pages: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    first_page: PropTypes.bool,
    last_page: PropTypes.bool,
    onClick: PropTypes.func
  }),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      is_sortable: PropTypes.bool,
      label: PropTypes.node,
      render: PropTypes.func,
      sort_by: PropTypes.func,
      styles: PropTypes.object
    })
  ).isRequired,
  data: PropTypes.array,
  defaultSortColumn: PropTypes.object,
  defaultSortDirection: PropTypes.string,
  hideHeaders: PropTypes.bool,
  onRowClick: PropTypes.func,
  onSortChange: PropTypes.func,
  perPage: PropTypes.number,
  placeholder: PropTypes.string,
  tableLayout: PropTypes.string
};

export default Table;
